import React, { FormEvent, useRef, useState } from 'react';
import { CYB } from '@wallets/sdk';
import { useTranslation } from 'react-i18next';

// hooks
import { useAppDispatch, useAppSelector } from 'lib/hooks/redux';
import { useRules } from 'lib/hooks/rules';
import { useForm } from 'react-hook-form';
import { useCountdown } from 'lib/hooks/timer';
import { useDisabledMessage } from 'lib/hooks/disabledMessage';
import { useDeliveryMethod } from 'lib/hooks/deliveryMethod';

// components
import PaymentMethodItem from 'components/payment/PaymentMethodItem';
import CybersourceOptions from 'components/payment/CybersourceOptions';
import CybersourceFormV2 from 'components/payment/CybersourceFormV2';

// store
import { setPaymentMethod } from 'store/paymentMethod/cybersource.slice';
import { RootState } from 'types/store';
import { setCard } from 'store/card/cybersource.slice';
import TokenizationConfirmationForm, {
    TokenizationConfirmationFormData,
} from 'components/payment/TokenizationConfirmationForm';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { convertSeconds } from './CybersourceAdder.utils';
import { checkAmount, doPretokenization, resetErrorPretokenizate } from 'store/card/card.slice';
import { resetPayments } from 'store/paymentMethod/actions';
import TokenizationConfirmationResult from 'components/payment/TokenizationConfirmationResult';
import { fetchCardsByUser } from 'store/cardToken';
import { updateInProgressCyb } from 'store/paymentMethod/paymentMethod.slice';
import { setFingerprint } from 'store/cart';
import { useAnalytics } from 'lib/hooks/analytics';

export function CybersourceAdder(): JSX.Element {
    const { t } = useTranslation('error');
    const dispatch = useAppDispatch();
    const analytics = useAnalytics();
    const dm = useDeliveryMethod();
    const pm = useAppSelector((state: RootState) => state.paymentMethod);
    const rules = useRules();
    const user = useAppSelector((state: RootState) => state.user);
    const token = useAppSelector((state: RootState) => state.auth).data?.accessToken;
    const cardExternal = useAppSelector(
        (state) => state.card.tokenizationCheckAmount.data.cardExternal,
    );
    const cyb = pm.types.cybersource;
    const couponLoading = useAppSelector((s) => s.cart.status !== 'ok');
    const checkAmountState = useAppSelector(
        (state: RootState) => state.card.tokenizationCheckAmount,
    );
    const pretokenizationState = useAppSelector((state: RootState) => state.card.pretokenization);
    const [isOpen, setIsOpen] = useState(false);
    // eslint-disable-next-line
    const onSubmit = useRef<null | any>(null);
    const [error, setError] = useState<null | string>(null);
    const [isSending, setIsSending] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [checkAmountIsLoading, setCheckAmountIsLoading] = useState(false);
    const isFullForm = true;
    const [remember, setRemember] = useState(true);
    const [tokenizationConfirmationFormIsOpen, setTokenizationConfirmationFormIsOpen] =
        useState(false);
    const [tokenizationConfirmationResultIsOpen, setTokenizationConfirmationResultIsOpen] =
        useState(false);
    const tokenizationConfirmationCountdown = useCountdown();

    const disabledMessage = useDisabledMessage().find((dm) => dm.paymentMethod === 'Cybersource')
        ?.disabledMessage;

    const form = useForm<TokenizationConfirmationFormData>({
        resolver: yupResolver(
            yup.object({
                confirmationAmount: yup.string().required(),
            }),
        ),
    });

    const [maxAttempts, setMaxAttempts] = useState(3);

    const handleOnClick = async () => {
        try {
            setIsLoading(true);
            setIsOpen(true);
            const payment = await dispatch(setPaymentMethod({})).unwrap();
            if (payment && user.data && token && payment.cart.shippingInfo) {
                setIsLoading(false);
                const micro = await CYB.initializeMicroformV2(payment.jwk, {}, isFullForm);

                const userForPayment = {
                    firstName: user.data.firstname,
                    lastName: user.data.lastname,
                    email: user.data.email,
                    phoneNumber: payment.cart.shippingInfo[0]?.customer.phoneNumber,
                };

                onSubmit.current = async () =>
                    await CYB.tokenizeCardWithMicroformV2(
                        micro,
                        userForPayment,
                        payment.id,
                        isFullForm,
                        token,
                    );

                analytics.push('checkout_interaction', undefined, 'begin_tokenization');
            }
        } catch (error) {
            setIsLoading(false);
            setError(t('cybDefaultError.message'));
        }
    };

    const handleOnSubmit = async (event: FormEvent) => {
        event.stopPropagation();
        setIsSending(true);
        setError(null);
        if (onSubmit.current) {
            const resp = await onSubmit.current();

            if (resp.data && resp.data.success) {
                await dispatch(setCard()).unwrap();

                if (remember) {
                    analytics.push('checkout_interaction', undefined, 'charge_tokenization');
                    dispatch(resetErrorPretokenizate());
                    setMaxAttempts(3);
                    setCheckAmountIsLoading(true);
                    setTokenizationConfirmationFormIsOpen(true);
                    await dispatch(setFingerprint()).unwrap();
                    await dispatch(doPretokenization());
                    setCheckAmountIsLoading(false);
                    setIsOpen(false);
                    setIsSending(false);
                }
            } else if (resp.error.code == 'CARD_NOT_ALLOWED_RIPLEY') {
                setError(t('cybCardNotAllowedRipleyError.message'));
            } else {
                setError(t('cybValidationError.message'));
            }

            setIsSending(false);
        }
    };

    const handleOnChangeRemember = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRemember(event.target.checked);
    };

    const isBlocked =
        pm.data.status === 'pending' ||
        isLoading ||
        couponLoading ||
        !dm.hasSchedules() ||
        pm.data.loadingPreferred;

    const isSelected =
        pm.data.status === 'ok' &&
        pm.data.selected.includes('Cybersource') &&
        !cyb.data?.isTokenized;

    const isDisabled = !rules.feature('Cybersource');

    React.useEffect(() => {
        if (tokenizationConfirmationFormIsOpen) {
            tokenizationConfirmationCountdown.startCountdown(Date.now() + 300000);
        }
        // eslint-disable-next-line
    }, [tokenizationConfirmationFormIsOpen]);

    const saveForm = async (data: TokenizationConfirmationFormData) => {
        await dispatch(checkAmount(data.confirmationAmount));
    };

    const onSubmitTokenizationConfirmation = (event: React.FormEvent) => {
        (async (event) => {
            setCheckAmountIsLoading(true);
            const handler = form.handleSubmit(saveForm);
            await handler(event);
            setMaxAttempts(maxAttempts - 1);
            setCheckAmountIsLoading(false);
            setTokenizationConfirmationFormIsOpen(false);
            setTokenizationConfirmationResultIsOpen(true);
        })(event);
    };

    const handleOnCloseTokenizationConfirmation = async () => {
        await dispatch(resetPayments()).unwrap();
        dispatch(resetErrorPretokenizate());
        form.reset();
        setTokenizationConfirmationFormIsOpen(false);
    };

    const handleOnCloseCybersourceFormV2 = async () => {
        await dispatch(resetPayments()).unwrap();
        setError(null);
        setIsOpen(false);
    };

    const successCheckAmount = async () => {
        analytics.push('checkout_interaction', undefined, 'sucess_tokenization');
        dispatch(updateInProgressCyb(true));
        setTokenizationConfirmationResultIsOpen(false);
        const cards = await dispatch(fetchCardsByUser()).unwrap();
        await dispatch(setPaymentMethod({ cardToken: cards.byId[cardExternal ?? ''] })).unwrap();
        await dispatch(setCard()).unwrap();
        dispatch(updateInProgressCyb(false));
        form.reset();
    };

    const handleTokenizationConfirmationResultOnClose = async () => {
        if (checkAmountState.data.success) {
            successCheckAmount();
            return;
        }

        form.reset();
        await dispatch(resetPayments()).unwrap();
        setTokenizationConfirmationResultIsOpen(false);
    };

    const handleTokenizationConfirmationOnAction = async () => {
        if (checkAmountState.data.success) {
            successCheckAmount();
            return;
        }

        form.reset();

        if (maxAttempts === 0) {
            handleTokenizationConfirmationResultOnClose();
        }

        if (maxAttempts > 0 && checkAmountState.data.errorMessage === 'invalid amount') {
            setTokenizationConfirmationResultIsOpen(false);
            setTokenizationConfirmationFormIsOpen(true);
        } else {
            handleTokenizationConfirmationResultOnClose();
        }
    };

    return (
        <>
            <PaymentMethodItem
                paymentMethodName="Tarjetas de Crédito"
                type="credito"
                widthIcon="63px"
                onClick={handleOnClick}
                blocked={isBlocked}
                isLoading={isLoading}
                selected={isSelected}
                disabled={disabledMessage ?? isDisabled}
                childrenIsModal={true}
            >
                {!cyb.data?.card?.installments && isOpen ? (
                    <CybersourceFormV2
                        isLoading={isLoading}
                        isSending={isSending}
                        onSubmit={handleOnSubmit}
                        error={error}
                        isOpen={isOpen}
                        remember={remember}
                        onClose={handleOnCloseCybersourceFormV2}
                        onChangeRemember={handleOnChangeRemember}
                    />
                ) : !remember ? (
                    <CybersourceOptions />
                ) : null}
            </PaymentMethodItem>
            <TokenizationConfirmationForm
                isLoading={checkAmountIsLoading}
                isOpen={tokenizationConfirmationFormIsOpen}
                form={form}
                error={pretokenizationState.data.success ? false : true}
                errorMessage={pretokenizationState.data.errorMessage}
                countdown={convertSeconds(tokenizationConfirmationCountdown.timeRemaining ?? 0)}
                onSubmit={onSubmitTokenizationConfirmation}
                onClose={handleOnCloseTokenizationConfirmation}
                attemps={maxAttempts}
            />
            <TokenizationConfirmationResult
                status={checkAmountState.data.success ? 'success' : 'error'}
                errorMessage={checkAmountState.data.errorMessage ?? 'default'}
                isOpen={tokenizationConfirmationResultIsOpen}
                onClose={handleTokenizationConfirmationResultOnClose}
                onAction={handleTokenizationConfirmationOnAction}
                attemps={maxAttempts}
            />
        </>
    );
}
